import {
  Box,
  Button,
  capitalize,
  Container,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import FormAutoComplete from "./../../Controllers/AutoComplete";
import FormTextField from "./../../Controllers/Input";
import service from "../public/gifService";
import { useHistory } from "react-router";
import CustomBox from "../public/Components/CustomBox";
import { GifColors } from "../../theme";
import authService from "../../services/authService";
import { useAuth } from "../../providers/authProvider";
import { useLoading } from "../../providers/loadingProvider";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  signupform: {
    backgroundColor: GifColors.lightBlue,
    borderRadius: 20,
    padding: 15,
    zIndex: 1,
  },
  autocomplete: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
    "& > *": {
      marginTop: theme.spacing(2),
    },
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  bigDots: {
    position: "relative",
    zIndex: 0,
    width: "100%",
  },
}));

const defaultState = {
  institutions: [],
  titles: [],
  programTypes: [],
  researchAreas: [],
  researchSubAreas: [],
  form: {},
};

export default function Register() {
  const methods = useForm();
  const { handleSubmit, watch, setValue, getValues, setError } = methods;
  const classes = useStyles();
  const [state, setState] = useState({ ...defaultState });
  const [registrationDone, setRegistrationDone] = useState(false);
  const history = useHistory();
  const loading = useLoading();
  const auth = useAuth();
  const [registering, setRegistering] = useState(false);
  const getInstitutions = useCallback(async () => {
    const institutions = (await service.getAllInstitutions()).map((i) => ({
      id: i.institutionId,
      label: i.name,
    }));
    setState((state) => ({ ...state, institutions }));
  }, []);

  const login = async () => {
    const redirectUrl = `${process.env.REACT_APP_HOMEPAGE}`;
    authService.signinRedirect(redirectUrl);
  };

  const getTitles = useCallback(async () => {
    const titles = (await service.GetContactTitles()).map((t) => ({
      id: t.value,
      label: t.label,
    }));

    setState((state) => ({ ...state, titles }));
  }, []);

  const getProgramTypes = useCallback(async () => {
    const programTypes = (await service.GetRegistrationPrograms()).map((t) => ({
      id: t.number,
      label: t.name,
    }));
    setState((state) => ({ ...state, programTypes }));
  }, []);

  const getReseachAreas = useCallback(async () => {
    const researchAreas = (await service.getResearchAreas()).map((t) => ({
      id: t.researchAreaCode,
      label: t.name,
      guid: t.researchAreaId,
    }));
    setState((state) => ({ ...state, researchAreas }));
  }, []);

  const researchAreaCodeWatcher = watch("researchAreaCode");
  useEffect(() => {
    const getResearchSubAreas = async () => {
      const area = state.researchAreas.find(
        (ra) => ra.id === researchAreaCodeWatcher
      );
      if (!area) {
        setState((state) => ({ ...state, researchSubAreas: [] }));
        return;
      }

      const researchSubAreas = (
        await service.getResearchSubAreas({
          areaId: area.guid,
        })
      ).map((sa) => ({
        id: sa.researchSubAreaCode,
        label: sa.name,
        guid: sa.researchSubAreaId,
      }));
      setState((state) => ({ ...state, researchSubAreas }));
    };
    getResearchSubAreas();
    setValue("researchSubAreaCode", "");
  }, [researchAreaCodeWatcher, setValue, state.researchAreas]);

  useEffect(() => {
    const loadingId = loading.addLoading("...");
    if (auth.isAuthenticated()) {
      history.push("/newproposal");
      loading.removeLoading(loadingId);
    } else {
      Promise.all([
        getProgramTypes(),
        getInstitutions(),
        getTitles(),
        getReseachAreas(),
      ]).then(() => loading.removeLoading(loadingId));
    }
  }, [
    getInstitutions,
    getReseachAreas,
    getTitles,
    getProgramTypes,
    auth,
    loading,
    history,
  ]);

  const onSubmit = async (data) => {
    const loadingId = loading.addLoading(
      "Registering... this may take a while"
    );
    try {
      setRegistering(true);
      if (!data.researchSubAreaCode) {
        data.researchSubAreaCode = "0";
      }
      const capData = {
        ...data,
        firstname: capitalize(data.firstname),
        lastname: capitalize(data.lastname),
      };
      service.register(capData).then((res) => {
        setRegistering(false);
        loading.removeLoading(loadingId);
        if (res === "user already exist") {
          setError(
            "email",
            {
              type: "manual",
              message: "Account with that Email is already exist",
            },
            { shouldFocus: true }
          );
        } else {
          setRegistrationDone(true);
        }
      });
    } catch (ex) {
      setRegistrationDone(false);
      setRegistering(false);
    }
  };

  const validatePasswordStrength = (password) => {
    let requirementsMet = 0;
    if (password.length < 8) return "Minimum length is 8 characters";
    if (/[@$!%*?&]/.test(password)) requirementsMet++;
    if (/[0-9]/.test(password)) requirementsMet++;
    if (/[a-z]/.test(password)) requirementsMet++;
    if (/[A-Z]/.test(password)) requirementsMet++;

    return (
      requirementsMet >= 3 ||
      "3 of 4 character classes are required - uppercase, lowercase, number, symbol."
    );
  };

  const validatePasswordMatch = (passwordValidation) => {
    const password = getValues("password");
    return passwordValidation === password || "Passwords missmatch";
  };

  const ValidateEmail = (mail) => {
    if (
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        mail
      )
    ) {
      return true;
    }
    return "You have entered an invalid email address";
  };
  const loginButton = (
    <Link style={{ cursor: "pointer" }} onClick={login} underline="hover">
      Log In
    </Link>
  );
  const registrationDoneComp = (
    <Box>
      <Typography variant="h6">Congratulation!</Typography>
      <Typography>
        Your registration for the GIF portal has been completed successfully.
        Please click the "Portal Login" button to start filling in the
        Pre-Proposal.
      </Typography>
      {loginButton}
    </Box>
  );
  return (
    <>
      <Box marginBottom={-55} position="relative" zIndex={1}>
        <CustomBox
          marginBottom={1}
          textAlign="center"
          fw={500}
          fs={17}
          color={GifColors.generalBlack}
        >
          SIGN UP TO GIF
        </CustomBox>
        <Container
          component="main"
          maxWidth="md"
          className={classes.signupform}
        >
          {registrationDone ? (
            registrationDoneComp
          ) : (
            <FormProvider {...methods}>
              <form
                className={classes.form}
                noValidate
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} lg={12}>
                    <FormAutoComplete
                      name="programType"
                      label="Program Type"
                      options={state.programTypes}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <FormAutoComplete
                      name="titleCode"
                      label="Title"
                      options={state.titles}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <FormTextField
                      name="firstname"
                      label="First Name"
                      required
                      inputProps={{ style: { textTransform: "capitalize" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <FormTextField
                      name="lastname"
                      label="Last Name"
                      required
                      inputProps={{ style: { textTransform: "capitalize" } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12}>
                    <FormAutoComplete
                      name="institutionId"
                      label="Institution"
                      options={state.institutions}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <FormAutoComplete
                      name="researchAreaCode"
                      label="Research Area"
                      options={state.researchAreas}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <FormAutoComplete
                      name="researchSubAreaCode"
                      label="Research Sub Area"
                      options={state.researchSubAreas}
                      required={state.researchSubAreas.length > 0}
                      disabled={state.researchSubAreas.length === 0}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <FormTextField
                      name="email"
                      label="Email"
                      validate={ValidateEmail}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} md={6} lg={6}>
                    <FormTextField
                      name="cellphone"
                      label="Cellphone"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <FormTextField
                      name="password"
                      label="Create Password"
                      type="password"
                      validate={validatePasswordStrength}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <FormTextField
                      name="password-validation"
                      label="Validate Password"
                      type="password"
                      validate={validatePasswordMatch}
                      required
                    />
                  </Grid>
                </Grid>
                <Button
                  disabled={registering}
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                >
                  {registering ? "Registering..." : "Sign up"}
                </Button>
                <Box textAlign="center">
                  <Typography style={{ fontWeight: 500 }}>
                    Already have an account?
                    {" "}
                    {loginButton}
                  </Typography>
                </Box>
              </form>
            </FormProvider>
          )}
        </Container>
      </Box>
      <img
        src="/images/gif-dots-large.svg"
        alt="dots"
        className={classes.bigDots}
      />
    </>
  );
}
